export function buildWeatherExportUrl(historyUrl, jobId = '') {
  const url = new URL(historyUrl);
  url.pathname = `${url.pathname.replace(/\/$/, '')}/exports${jobId ? `/${encodeURIComponent(jobId)}` : ''}`;
  url.search = '';
  url.hash = '';
  return url.toString();
}

function abortError() {
  return new DOMException('Export polling stopped.', 'AbortError');
}

export function waitForExportPoll(delayMs, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const abort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, delayMs);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export async function requestWeatherHistoryExport({
  historyUrl, apiKey, stationId, from, to, signal, onProgress,
}) {
  const headers = { 'content-type': 'application/json', 'x-weatherstation-key': apiKey };
  let jobId = '';
  let failures = 0;
  let initial = true;
  while (true) {
    if (signal?.aborted) throw abortError();
    const response = await fetch(buildWeatherExportUrl(historyUrl, jobId), {
      method: initial ? 'POST' : 'GET',
      headers,
      ...(initial ? { body: JSON.stringify({ stationId, from, to }) } : {}),
      signal,
    });
    if (response.status === 429 || response.status === 503) {
      failures += 1;
      if (failures > 5) throw new Error('The export service is busy. Please try again shortly.');
      const retrySeconds = Number(response.headers?.get?.('retry-after'));
      const delayMs = Number.isFinite(retrySeconds) && retrySeconds > 0
        ? Math.min(retrySeconds * 1000, 30000)
        : Math.min(3000 * (2 ** (failures - 1)), 30000);
      await waitForExportPoll(delayMs, signal);
      continue;
    }
    const payload = await response.json();
    if (!response.ok || payload?.ok === false) {
      throw new Error(payload?.error || payload?.message || 'Unable to prepare the weather export.');
    }
    if (signal?.aborted) throw abortError();
    failures = 0;
    if (initial) {
      jobId = payload?.jobId;
      if (!jobId) throw new Error('The export service did not return a job ID.');
      initial = false;
    }
    onProgress?.(payload);
    if (payload.status === 'ready') {
      if (!payload.downloadUrl) throw new Error('The completed export has no download link.');
      const downloadUrl = new URL(payload.downloadUrl);
      if (downloadUrl.protocol !== 'https:') throw new Error('The export download link is invalid.');
      return payload;
    }
    if (payload.status === 'failed' || payload.status === 'cancelled') {
      throw new Error(payload.error || (payload.status === 'cancelled' ? 'The export was cancelled.' : 'The export failed.'));
    }
    if (payload.status !== 'queued' && payload.status !== 'running') {
      throw new Error('The export service returned an unknown job status.');
    }
    await waitForExportPoll(3000, signal);
  }
}

export function downloadWeatherExport(result) {
  const link = document.createElement('a');
  link.href = result.downloadUrl;
  link.download = result.fileName || 'weather-readings.csv';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
